import React, { useState } from 'react';
import { cn } from '@/utils/cn';
import { TargetedImagePicker } from '@/components/media/TargetedImagePicker';
import { CroppedThumbnail } from '@/components/media/CroppedThumbnail';
import { pendingKey, type PendingTargeted } from '@/types/media';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { ImageThumbnailWithActions } from '@/components/common/ImageThumbnailWithActions';

/**
 * Display Ad Creation Step
 *
 * Creates a Responsive Display Ad (RDA) which requires:
 * - Marketing images (landscape 1.91:1 + square 1:1 required)
 * - Logos (optional, square 1:1 or landscape 4:1)
 * - Short headlines (1-5, max 30 chars each)
 * - Long headline (1, max 90 chars)
 * - Descriptions (1-5, max 90 chars each)
 * - Business name (max 25 chars)
 * - Final URL
 */

export interface DisplayAdFormData {
  landscapePending: PendingTargeted[];
  squarePending: PendingTargeted[];
  logoSquarePending: PendingTargeted[];
  logoLandscapePending: PendingTargeted[];
  shortHeadlines: string[];
  longHeadline: string;
  descriptions: string[];
  businessName: string;
  finalUrl: string;
}

interface DisplayAdCreationStepProps {
  formData: DisplayAdFormData;
  onChangeFormData: (data: Partial<DisplayAdFormData>) => void;
  errors?: {
    landscapeImages?: string;
    squareImages?: string;
    shortHeadlines?: string;
    longHeadline?: string;
    descriptions?: string;
    businessName?: string;
    finalUrl?: string;
  };
}

// Pending-crop preview (CSS-cropped, no GCS write yet).
interface PendingPreviewProps {
  pending: PendingTargeted;
  onRemove: () => void;
  aspectRatio?: string;
}

function PendingPreview({ pending, onRemove, aspectRatio }: PendingPreviewProps) {
  const getSize = () => {
    switch (aspectRatio) {
      case '1.91:1': return { width: 76, height: 40 };
      case '1:1': return { width: 64, height: 64 };
      case '4:1': return { width: 96, height: 24 };
      default: return { width: 64, height: 64 };
    }
  };
  return (
    <ImageThumbnailWithActions
      alt={pending.source.alt_text || pending.source.original_filename}
      size={getSize()}
      onRemove={onRemove}
      removeLabel="Remove image"
      confirmTitle="Remove Image"
      confirmMessage="Are you sure you want to remove this image?"
    >
      <CroppedThumbnail source={pending.source} cropBox={pending.cropBox} />
    </ImageThumbnailWithActions>
  );
}

export const DisplayAdCreationStep: React.FC<DisplayAdCreationStepProps> = ({
  formData,
  onChangeFormData,
  errors,
}) => {
  // Image picker state. Targeted creation is deferred until the wizard
  // submits — picker confirmation just stores PendingTargeted[] in form data.
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerMode, setImagePickerMode] = useState<'landscape' | 'square' | 'logoSquare' | 'logoLandscape'>('landscape');

  // Handlers for text fields
  const handleShortHeadlineChange = (index: number, value: string) => {
    const updated = [...formData.shortHeadlines];
    updated[index] = value;
    onChangeFormData({ shortHeadlines: updated });
  };

  const handleDescriptionChange = (index: number, value: string) => {
    const updated = [...formData.descriptions];
    updated[index] = value;
    onChangeFormData({ descriptions: updated });
  };

  const addShortHeadline = () => {
    if (formData.shortHeadlines.length < 5) {
      onChangeFormData({ shortHeadlines: [...formData.shortHeadlines, ''] });
    }
  };

  const removeShortHeadline = (index: number) => {
    if (formData.shortHeadlines.length > 1) {
      onChangeFormData({ shortHeadlines: formData.shortHeadlines.filter((_, i) => i !== index) });
    }
  };

  const addDescription = () => {
    if (formData.descriptions.length < 5) {
      onChangeFormData({ descriptions: [...formData.descriptions, ''] });
    }
  };

  const removeDescription = (index: number) => {
    if (formData.descriptions.length > 1) {
      onChangeFormData({ descriptions: formData.descriptions.filter((_, i) => i !== index) });
    }
  };

  // Open image picker for specific type
  const openImagePicker = (mode: typeof imagePickerMode) => {
    setImagePickerMode(mode);
    setShowImagePicker(true);
  };

  // Picker confirmation — store pending crops in form data (in memory).
  const handleImageConfirm = (pending: PendingTargeted[]) => {
    switch (imagePickerMode) {
      case 'landscape':
        onChangeFormData({ landscapePending: pending });
        break;
      case 'square':
        onChangeFormData({ squarePending: pending });
        break;
      case 'logoSquare':
        onChangeFormData({ logoSquarePending: pending });
        break;
      case 'logoLandscape':
        onChangeFormData({ logoLandscapePending: pending });
        break;
    }
    setShowImagePicker(false);
  };

  const placementForMode: Record<typeof imagePickerMode, string> = {
    landscape: 'marketing_image',
    square: 'square_marketing_image',
    logoSquare: 'logo',
    logoLandscape: 'landscape_logo',
  };
  const MAX_SELECTION_FOR_MODE: Record<typeof imagePickerMode, number> = {
    landscape: 15,
    square: 15,
    logoSquare: 5,
    logoLandscape: 5,
  };
  const pickerConfig = { maxSelection: MAX_SELECTION_FOR_MODE[imagePickerMode] };

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Your Display Ad</h3>
        <p className="text-sm text-gray-600">
          Create a Responsive Display Ad with images, headlines, and descriptions.
          Google will automatically optimize combinations for best performance.
        </p>
      </div>

      {/* Ad Type Info */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-center">
          <div className="h-8 w-8 bg-purple-100 rounded-full flex items-center justify-center mr-3">
            <PhotoIcon className="h-4 w-4 text-purple-600" />
          </div>
          <div>
            <span className="block text-sm font-medium text-gray-900">Responsive Display Ad</span>
            <p className="text-xs text-gray-500">
              Images, headlines, and descriptions are combined automatically for optimal display placements
            </p>
          </div>
        </div>
      </div>

      {/* Images Section */}
      <div className="space-y-4 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-900">Images</h4>

        {/* Landscape Images (Required) */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Landscape Images (1.91:1) <span className="text-red-500">*</span>
            </label>
            <span className="text-xs text-gray-500">
              {formData.landscapePending.length}/15 (min 1 required)
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.landscapePending.map((p) => (
              <PendingPreview
                key={pendingKey(p)}
                pending={p}
                aspectRatio="1.91:1"
                onRemove={() => {
                  const k = pendingKey(p);
                  onChangeFormData({
                    landscapePending: formData.landscapePending.filter((q) => pendingKey(q) !== k)
                  });
                }}
              />
            ))}
            <button
              type="button"
              onClick={() => openImagePicker('landscape')}
              className="w-20 h-10 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
            >
              <PhotoIcon className="w-5 h-5" />
            </button>
          </div>
          {errors?.landscapeImages && (
            <p className="text-sm text-red-600">{errors.landscapeImages}</p>
          )}
          <p className="text-xs text-gray-500">Recommended: 1200x628px, max 5MB</p>
        </div>

        {/* Square Images (Required) */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Square Images (1:1) <span className="text-red-500">*</span>
            </label>
            <span className="text-xs text-gray-500">
              {formData.squarePending.length}/15 (min 1 required)
            </span>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.squarePending.map((p) => (
              <PendingPreview
                key={pendingKey(p)}
                pending={p}
                aspectRatio="1:1"
                onRemove={() => {
                  const k = pendingKey(p);
                  onChangeFormData({
                    squarePending: formData.squarePending.filter((q) => pendingKey(q) !== k)
                  });
                }}
              />
            ))}
            <button
              type="button"
              onClick={() => openImagePicker('square')}
              className="w-16 h-16 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
            >
              <PhotoIcon className="w-5 h-5" />
            </button>
          </div>
          {errors?.squareImages && (
            <p className="text-sm text-red-600">{errors.squareImages}</p>
          )}
          <p className="text-xs text-gray-500">Recommended: 1200x1200px, max 5MB</p>
        </div>

        {/* Logos (Optional) */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Square Logo (1:1)
              </label>
              <span className="text-xs text-gray-500">
                {formData.logoSquarePending.length}/5
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.logoSquarePending.map((p) => (
                <PendingPreview
                  key={pendingKey(p)}
                  pending={p}
                  aspectRatio="1:1"
                  onRemove={() => {
                    const k = pendingKey(p);
                    onChangeFormData({
                      logoSquarePending: formData.logoSquarePending.filter((q) => pendingKey(q) !== k)
                    });
                  }}
                />
              ))}
              <button
                type="button"
                onClick={() => openImagePicker('logoSquare')}
                className="w-12 h-12 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
              >
                <PhotoIcon className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500">Optional, 1200x1200px recommended</p>
          </div>
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Landscape Logo (4:1)
              </label>
              <span className="text-xs text-gray-500">
                {formData.logoLandscapePending.length}/5
              </span>
            </div>
            <div className="flex flex-wrap gap-2 mb-2">
              {formData.logoLandscapePending.map((p) => (
                <PendingPreview
                  key={pendingKey(p)}
                  pending={p}
                  aspectRatio="4:1"
                  onRemove={() => {
                    const k = pendingKey(p);
                    onChangeFormData({
                      logoLandscapePending: formData.logoLandscapePending.filter((q) => pendingKey(q) !== k)
                    });
                  }}
                />
              ))}
              <button
                type="button"
                onClick={() => openImagePicker('logoLandscape')}
                className="w-20 h-5 border-2 border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 transition-colors"
              >
                <PhotoIcon className="w-3 h-3" />
              </button>
            </div>
            <p className="text-xs text-gray-500">Optional, 1200x300px recommended</p>
          </div>
        </div>
      </div>

      {/* Text Assets Section */}
      <div className="space-y-4 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-900">Text Assets</h4>

        {/* Business Name */}
        <div>
          <label htmlFor="business-name" className="block text-sm font-medium text-gray-700 mb-2">
            Business Name <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              id="business-name"
              value={formData.businessName}
              onChange={(e) => onChangeFormData({ businessName: e.target.value })}
              placeholder="Your Company Name"
              maxLength={25}
              className={cn(
                'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
                { 'border-red-500': errors?.businessName }
              )}
            />
            <span className="text-xs text-gray-500 self-center min-w-[40px]">
              {formData.businessName.length}/25
            </span>
          </div>
          {errors?.businessName && (
            <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>
          )}
        </div>

        {/* Final URL */}
        <div>
          <label htmlFor="final-url" className="block text-sm font-medium text-gray-700 mb-2">
            Final URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            id="final-url"
            value={formData.finalUrl}
            onChange={(e) => onChangeFormData({ finalUrl: e.target.value })}
            placeholder="https://www.example.com/landing-page"
            className={cn(
              'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
              { 'border-red-500': errors?.finalUrl }
            )}
          />
          {errors?.finalUrl && <p className="mt-1 text-sm text-red-600">{errors.finalUrl}</p>}
        </div>

        {/* Short Headlines */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Short Headlines <span className="text-red-500">*</span>
            </label>
            <span className="text-xs text-gray-500">
              {formData.shortHeadlines.length}/5 (min 1 required)
            </span>
          </div>
          <div className="space-y-2">
            {formData.shortHeadlines.map((headline, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={headline}
                  onChange={(e) => handleShortHeadlineChange(index, e.target.value)}
                  placeholder={`Headline ${index + 1}`}
                  maxLength={30}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-500 self-center min-w-[40px]">
                  {headline.length}/30
                </span>
                {formData.shortHeadlines.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeShortHeadline(index)}
                    className="text-red-600 hover:text-red-700 px-2"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
          {errors?.shortHeadlines && (
            <p className="mt-1 text-sm text-red-600">{errors.shortHeadlines}</p>
          )}
          {formData.shortHeadlines.length < 5 && (
            <button
              type="button"
              onClick={addShortHeadline}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700"
            >
              + Add headline
            </button>
          )}
        </div>

        {/* Long Headline */}
        <div>
          <label htmlFor="long-headline" className="block text-sm font-medium text-gray-700 mb-2">
            Long Headline <span className="text-red-500">*</span>
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              id="long-headline"
              value={formData.longHeadline}
              onChange={(e) => onChangeFormData({ longHeadline: e.target.value })}
              placeholder="A longer headline that can stand alone"
              maxLength={90}
              className={cn(
                'flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500',
                { 'border-red-500': errors?.longHeadline }
              )}
            />
            <span className="text-xs text-gray-500 self-center min-w-[40px]">
              {formData.longHeadline.length}/90
            </span>
          </div>
          {errors?.longHeadline && (
            <p className="mt-1 text-sm text-red-600">{errors.longHeadline}</p>
          )}
        </div>

        {/* Descriptions */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Descriptions <span className="text-red-500">*</span>
            </label>
            <span className="text-xs text-gray-500">
              {formData.descriptions.length}/5 (min 1 required)
            </span>
          </div>
          <div className="space-y-2">
            {formData.descriptions.map((description, index) => (
              <div key={index} className="flex gap-2">
                <textarea
                  value={description}
                  onChange={(e) => handleDescriptionChange(index, e.target.value)}
                  placeholder={`Description ${index + 1}`}
                  maxLength={90}
                  rows={2}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-xs text-gray-500 self-start mt-2 min-w-[40px]">
                  {description.length}/90
                </span>
                {formData.descriptions.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeDescription(index)}
                    className="text-red-600 hover:text-red-700 px-2 self-start mt-2"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
          {errors?.descriptions && (
            <p className="mt-1 text-sm text-red-600">{errors.descriptions}</p>
          )}
          {formData.descriptions.length < 5 && (
            <button
              type="button"
              onClick={addDescription}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700"
            >
              + Add description
            </button>
          )}
        </div>
      </div>

      {/* Image Picker Modal — returns PendingTargeted[]; no GCS write here. */}
      <TargetedImagePicker
        isOpen={showImagePicker}
        onClose={() => setShowImagePicker(false)}
        usageContext={{
          platform: 'google_ads',
          placementKey: `google_ads.${placementForMode[imagePickerMode]}`,
        }}
        onConfirm={handleImageConfirm}
        maxSelection={pickerConfig.maxSelection}
      />
    </div>
  );
};
